<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" @click="$emit('close')"></div>
    </Transition>

    <Transition name="slide-over">
      <div v-if="open" class="fixed inset-y-0 right-0 z-50 w-full sm:w-[62%] bg-white dark:bg-[#1C2128] shadow-2xl flex flex-col">

        <div class="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">
              {{ loading ? 'Applicants' : `${records.length} ${records.length === 1 ? 'applicant' : 'applicants'}` }}
            </h2>
            <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ jobTitle }}</p>
          </div>
          <button type="button" @click="$emit('close')"
            class="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Close
          </button>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5">

          <div v-if="loading" class="animate-pulse space-y-3">
            <div v-for="n in 4" :key="n" class="flex items-center gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div class="flex-1 space-y-2">
                <div class="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
              <div class="w-24 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>

          <div v-else-if="error" class="py-24 text-center">
            <p class="text-base font-semibold text-gray-900 dark:text-white">Could not load applicants</p>
            <p class="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{{ error }}</p>
          </div>

          <div v-else-if="records.length === 0" class="py-24 text-center">
            <p class="text-base font-semibold text-gray-900 dark:text-white">No applications yet</p>
            <p class="mx-auto mt-2 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              They will appear here as they come in.
            </p>
          </div>

          <ul v-else class="space-y-3">
            <li v-for="application in records" :key="application.id"
              class="flex flex-wrap items-center gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <span class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20 text-sm font-bold text-blue-600 dark:text-blue-400">
                {{ (application.full_name || '?').trim().charAt(0).toUpperCase() }}
              </span>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ application.full_name }}</p>
                <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                  <a :href="`mailto:${application.email}`" class="text-blue-600 dark:text-blue-400 hover:underline">{{ application.email }}</a>
                  <span class="mx-1.5" aria-hidden="true">&middot;</span>{{ application.cell_phone }}
                </p>
              </div>
              <span class="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{{ formatDate(application.applied_at) }}</span>
              <a v-if="application.resume_url" :href="application.resume_url"
                class="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
                Download CV
              </a>
              <span v-else class="text-xs text-gray-300 dark:text-gray-600">No CV</span>
            </li>
          </ul>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
defineProps({
  open: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  jobTitle: { type: String, default: '' },
  records: { type: Array, default: () => [] },
})

defineEmits(['close'])

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to    { opacity: 0; }
</style>
